import { useEffect, useState } from "react"
import type { Result } from "@ethersproject/abi"
import { defaultAbiCoder, Interface } from "@ethersproject/abi"
import { hexDataSlice } from "@ethersproject/bytes"
import type { AbiItem } from "web3-utils"

import type { Calldata } from "proposal/types/calldata"
import type { ChainId, Maybe } from "query/graphql"
import { useContractData } from "createProposal/hooks/useContractData"
import type { FourBytesResponse } from "proposal/types/signature"
import { EXTERNAL_ENDPOINTS } from "common/constants/endpoints"
import { fetcher } from "common/helpers/fetcher"
import { jsonParse, jsonStringify } from "common/helpers/serialization"
import { handleParsingError } from "common/helpers/error"
import { useAsyncError } from "common/hooks/useAsyncError"
import { useToast } from "common/hooks/useToast"
import { getNextType } from "createProposal/helpers/abi"

export enum DecodeError {
  CONTRACT_NOT_VERIFIED,
  INVALID_FUNCTION_SIGNATURE,
  NO_FUNCTION_SIGNATURE,
  FETCH_CONTRACT_EXCEPTION,
  DECODE_CONTRACT_ERROR,
  FOUR_BYTE_API_ERROR,
}

type DecodeStatus = {
  success: boolean
  error?: DecodeError
}

type ParsedSignature = {
  name: string
  types: string[] | undefined
}

export const errors: Record<DecodeError, string> = {
  [DecodeError.CONTRACT_NOT_VERIFIED]:
    "This function is from an unverified contract.",
  [DecodeError.INVALID_FUNCTION_SIGNATURE]:
    "This function signature was not found in the contract ABI.",
  [DecodeError.NO_FUNCTION_SIGNATURE]:
    "This function signature could not be found.",
  [DecodeError.FETCH_CONTRACT_EXCEPTION]:
    "An exception occurred when fetching the contract ABI.",
  [DecodeError.DECODE_CONTRACT_ERROR]:
    "There is an error trying to decode the contract.",
  [DecodeError.FOUR_BYTE_API_ERROR]:
    "An exception occurred when fetching the method signature from 4Byte.",
}

type Args = {
  chainId: ChainId
  customAbiMethods?: AbiItem[]
  calldata: string
  target: string
  signature?: Maybe<string>
}

type Values = {
  derivedSignature?: string
  decodedCalldata?: Calldata[]
  isSendAssetRecipe: boolean
  status?: DecodeStatus
}

export const useParseProposalAction = ({
  chainId,
  customAbiMethods,
  calldata,
  target,
  signature,
}: Args): Values => {
  const [status, setStatus] = useState<DecodeStatus>()
  const [decodedCalldata, setDecodedCalldata] = useState<Calldata[]>()
  const [signatures, setSignatures] = useState<string[]>()
  const [derivedSignature, setDerivedSignature] = useState<string>()
  const asyncError = useAsyncError()

  const { data, getContractData } = useContractData()
  const { toast } = useToast()

  const isSendAssetRecipe = calldata === "0x"

  useEffect(() => {
    if (signature || isSendAssetRecipe) {
      return
    }

    const getCommandSignature = async (calldata: string): Promise<string[]> => {
      const hexSignatureCode = calldata.substring(0, 9)

      const response = await fetcher.rest<FourBytesResponse | undefined>({
        endpoint: EXTERNAL_ENDPOINTS.fourByte(hexSignatureCode),
        error: {
          warning: {
            isSilent: true,
          },
        },
        onError: () =>
          setStatus({
            success: false,
            error: DecodeError.FOUR_BYTE_API_ERROR,
          }),
      })

      return response?.results.map((result) => result.text_signature) ?? []
    }

    getCommandSignature(calldata).then((signatures) => {
      if (signatures && signatures.length) {
        setSignatures(signatures)
      }
    })
  }, [signature, isSendAssetRecipe, calldata, toast, asyncError])

  useEffect(() => {
    getContractData(target, chainId)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, chainId])

  const parseSignature = (signature: string): ParsedSignature => {
    const [name, argsWithClosingParen] = signature.split("(")
    const args = argsWithClosingParen.slice(0, -1)
    const types = args ? args.split(",") : undefined

    return { name, types }
  }

  const generateCalldata = (
    signature: string,
    types: string[],
    result: Result,
  ): void => {
    const decodedCalldata = types
      ? types.reduce<Calldata[]>((acc, type: string, index: number) => {
          return [
            ...acc,
            {
              type,
              value: result?.[index],
            },
          ]
        }, [])
      : []

    setDecodedCalldata(decodedCalldata)
    setDerivedSignature(signature)
    setStatus(() => ({ success: true }))
  }

  useEffect(() => {
    if (isSendAssetRecipe) {
      if (signature) setDerivedSignature(signature)

      return
    }

    if (signature) {
      const { types, name } = parseSignature(signature)

      if (!types) return

      const SIG_PREFIX_LENGTH = 4

      const sigHash = hexDataSlice(calldata, 0, SIG_PREFIX_LENGTH)
      const calldataWithoutSigHash = hexDataSlice(calldata, SIG_PREFIX_LENGTH)

      const NULL_SIG_HASH = "0x00000000"

      const _data =
        sigHash === NULL_SIG_HASH ? calldata : calldataWithoutSigHash

      try {
        const abiMethods: AbiItem[] = data?.contractData
          ? jsonParse(data.contractData)
          : null

        const getType = (type: string, idx: number): string => {
          if (!abiMethods) return type

          const abiMethod = abiMethods.find(
            (abiMethod) => abiMethod.name === name,
          )

          if (!abiMethod || !abiMethod.inputs) return type

          if (type === "tuple[]") {
            const fixedType = getNextType(
              type,
              abiMethod.inputs[idx].components,
            )

            return fixedType
          }

          if (type === "tuple") {
            const fixedType = getNextType(
              type,
              abiMethod.inputs[idx].components,
            )

            return fixedType
          }

          return type
        }

        const _types = abiMethods
          ? types.map((type, idx) => getType(type, idx))
          : types

        const decoded = defaultAbiCoder.decode(_types, _data)

        generateCalldata(signature, types, decoded)
      } catch (error) {
        setStatus({
          success: false,
          error: DecodeError.DECODE_CONTRACT_ERROR,
        })
      }

      return
    }

    if (!signatures) {
      setStatus({
        success: false,
        error: DecodeError.NO_FUNCTION_SIGNATURE,
      })

      return
    }

    if (!data) {
      setStatus({
        success: false,
        error: DecodeError.FETCH_CONTRACT_EXCEPTION,
      })

      return
    }

    const { contractData, isContractVerified } = data

    if (!isContractVerified || !contractData) {
      setStatus({
        success: false,
        error: DecodeError.CONTRACT_NOT_VERIFIED,
      })

      return
    }

    const abiMethods: AbiItem[] = jsonParse(contractData)
    const allAbiItems = customAbiMethods
      ? [...abiMethods, ...customAbiMethods]
      : abiMethods
    const contractInterface = new Interface(jsonStringify(allAbiItems))

    signatures.forEach((signature) => {
      const { name, types } = parseSignature(signature)

      const isSignatureValid = allAbiItems.some(
        (abiMethod) => abiMethod.name === name,
      )

      if (isSignatureValid) {
        try {
          const decoded = contractInterface.decodeFunctionData(name, calldata)

          if (!types) {
            generateCalldata(signature, [], decoded)

            return
          }

          generateCalldata(signature, types, decoded)

          return
        } catch (error) {
          handleParsingError(error)
        }
      }

      setStatus((prev) => {
        if (prev?.success) {
          return prev
        }

        return {
          success: false,
          error: DecodeError.INVALID_FUNCTION_SIGNATURE,
        }
      })
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, signatures, isSendAssetRecipe])

  return {
    derivedSignature,
    decodedCalldata,
    isSendAssetRecipe,
    status,
  }
}
